import React, { useEffect } from 'react';
import type { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import CompanyProfileLayout from 'src/components/company/CompanyProfileLayout';
import CompanyAboutSection from 'src/components/company/CompanyAboutSection';
import CompanyAdsSection from 'src/components/company/CompanyAdsSection';
import CompanyGalleriesSection from 'src/components/company/CompanyGalleriesSection';
import CompanyPostsSection from 'src/components/company/CompanyPostsSection';
import CompanyProjectsSection from 'src/components/company/CompanyProjectsSection';
import Loader from 'src/components/ui/Loader';
import Divider from 'src/components/ui/Divider';
import EmptyState from 'src/components/ui/EmptyState';

import { useCompanyProfileQuery } from 'src/hooks/__generated__/queries';
import useAppRouter from 'src/hooks/useAppRouter';
import RatingSection from 'src/components/profile/RatingSection';

import { routePath404, routePathCompany } from 'src/constants/router';

import { isContentTypeDisplayable } from 'src/utils/companyUtils';
import CompanyJobsOffersSection from 'src/components/company/CompanyJobOffersSection';
import { getAuthCookie } from 'src/utils/authCookie';
import getApolloClient from 'src/utils/getApolloClient';

import type { CompanyProfileQuery } from 'src/types/__generated__/graphql';
import { CompanyProfile, ContentType } from 'src/types/__generated__/graphql';
import useBreakpoints from 'src/hooks/useBreakpoints';

const CompanyHomePage: PageComponent = () => {
    const router = useAppRouter();
    const companyId = router.query.companyId as string;
    const { anchor } = router.query;
    const { t } = useTranslation(['company']);

    const { isMobile } = useBreakpoints();

    const { previousData, data = previousData } = useCompanyProfileQuery({
        variables: {
            companyId: companyId as string,
        },
        skip: !companyId,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        const scrollToAnchor = async () => {
            await router.replace(`${routePathCompany}/${companyId}#${anchor}`);
        };
        if (anchor && data && anchor !== 'team-section') {
            // as we display only the loader if no data, we need to push to the good section when data is loaded and HTML for this section is in the page
            scrollToAnchor().catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchor, data]);

    const noEmptyAdsSection =
        Number(data?.companyProfile.numberOfAvailableAds) > 0 || Number(data?.companyProfile.numberOfEndedAds) > 0;
    const noEmptyGalleriesSection = Number(data?.companyProfile.numberOfGalleries) > 0;
    const noEmptyPostsSection = Number(data?.companyProfile.numberOfPosts) > 0;
    const noEmptyProjectsSection = Number(data?.companyProfile.numberOfProjects) > 0;
    const noEmptyJobOffersSection = Number(data?.companyProfile.numberOfJobOffers) > 0;
    const noEmptyStateHome =
        noEmptyProjectsSection ||
        noEmptyAdsSection ||
        noEmptyGalleriesSection ||
        noEmptyPostsSection ||
        noEmptyJobOffersSection;

    if (!data) {
        return <Loader />;
    }

    return (
        <Box pb={6}>
            <CompanyAboutSection
                description={data.companyProfile.description as string}
                createdAt={data.companyProfile.createdAt as string}
            />
            <Divider />

            {noEmptyStateHome ? (
                <>
                    {noEmptyAdsSection && isContentTypeDisplayable(ContentType.Ad, data.companyProfile) && (
                        <>
                            <CompanyAdsSection companyProfile={data.companyProfile} isMobile={isMobile} />
                            <Divider />
                        </>
                    )}

                    {noEmptyProjectsSection && isContentTypeDisplayable(ContentType.Project, data.companyProfile) && (
                        <>
                            <CompanyProjectsSection companyProfile={data.companyProfile} />
                            <Divider />
                        </>
                    )}

                    {noEmptyGalleriesSection && isContentTypeDisplayable(ContentType.Gallery, data.companyProfile) && (
                        <>
                            <CompanyGalleriesSection companyProfile={data.companyProfile} />
                            <Divider />
                        </>
                    )}

                    {noEmptyPostsSection && isContentTypeDisplayable(ContentType.Post, data.companyProfile) && (
                        <>
                            <CompanyPostsSection companyProfile={data.companyProfile} />
                            <Divider />
                        </>
                    )}

                    {noEmptyJobOffersSection && isContentTypeDisplayable(ContentType.JobOffer, data.companyProfile) && (
                        <>
                            <CompanyJobsOffersSection companyProfile={data.companyProfile} />
                            <Divider />
                        </>
                    )}
                </>
            ) : (
                <>
                    <Box mt="3rem" mb="2rem">
                        <EmptyState icon="Subtract" text1={t('company:noContent')} text2={t('company:findContent')} />
                    </Box>
                    <Divider />
                </>
            )}
            <Box id="rating-section">
                <RatingSection refetchQueries={['CompanyProfile', 'Ratings']} company={data.companyProfile} />
            </Box>
        </Box>
    );
};

CompanyHomePage.Layout = CompanyProfileLayout;

interface CompanyPageProps extends PageProps {
    company?: CompanyProfileQuery['companyProfile'];
}

export const getServerSideProps: GetServerSideProps<CompanyPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const companyId = context.params?.companyId as string | undefined;
    let data;
    if (companyId) {
        try {
            const result = await apolloClient.query<CompanyProfileQuery>({
                query: CompanyProfile,
                variables: {
                    companyId,
                },
            });
            data = result.data;
        } catch {
            // As the CompanyProfile query returns the company or throws an error, we need to catch it to return a 404 page
            return {
                redirect: {
                    permanent: false,
                    destination: routePath404,
                },
                props: {},
            };
        }
    }

    const description =
        data?.companyProfile.description && data.companyProfile.description.length > 0
            ? data.companyProfile.description.substring(0, 200)
            : ' ';
    return {
        props: {
            company: data?.companyProfile,
            ...(context.locale
                ? await serverSideTranslations(context.locale, [
                      'common',
                      'company',
                      'profile',
                      'edit-profile',
                      'ad',
                      'favorites',
                      'news',
                      'notifications',
                      'contacts',
                  ])
                : {}),
            SEOProps: {
                pageTitleTranslation: {
                    key: 'common:seo.company.title',
                    params: {
                        name: String(data?.companyProfile.name),
                    },
                },
                metaTitleTranslation: {
                    key: 'common:seo.company.title',
                    params: {
                        name: String(data?.companyProfile.name),
                    },
                },
                metaDescriptionTranslation: {
                    value: description,
                },
                imageUrl: data?.companyProfile.image?.url ?? null,
            },
        },
    };
};
export default CompanyHomePage;
